import { Box, CircularProgress, Typography } from "@mui/material"
import { useMemo } from "react"
import {
  useGetRunResultQuery,
  useGetRunsNodesResultsHistoricQuery,
} from "@store/services/runService"
import { NodeResultsHistoricValue } from "@custom-types/modelRun"
import { getMaterialTableColumns } from "./const"
import { Table } from "@components/Table"
import { format } from "date-fns"
import { formatValuePythonic } from "@utils/format/pythonicFormat"
import { GroupNodeLabel } from "@features/graph/graphViewModel"
import { useParams } from "react-router"
import { graphViewGroupNodeLabelToSampleNode } from "@features/graph/utils/graphUtils"
import { getNodeResults } from "@features/results/Result/runResult.utils"
import logger from "@utils/logger"

const formatTimestep = (timestep: string) => {
  const timestepDate = new Date(
    [timestep.slice(0, 4), timestep.slice(4, 6), timestep.slice(6, 8)].join("-"),
  )
  return format(timestepDate, "yyyy-MM-dd")
}

type NodeResultRow = {
  timestep: string
  node_key: string
  data: number[] | string[]
}

const getTableRows = (
  results: NodeResultsHistoricValue | undefined,
  nodeLabel: string,
): NodeResultRow[] => {
  if (!results) return []

  return Object.entries(results.timesteps ?? {}).map(([timestep, data]) => ({
    timestep: formatTimestep(timestep),
    node_key: nodeLabel,
    data,
  }))
}

type RawResultsTableProps = {
  node: GroupNodeLabel
  runId: string
  lagShift: number
}

export default function RawResultsTable({ runId, node, lagShift }: RawResultsTableProps) {
  const { id } = useParams()

  const { isFetching: isFetchingRunData, data: runData } = useGetRunResultQuery(id || "")
  const { isFetching: isFetchingHistoricData, data: historicResponse } =
    useGetRunsNodesResultsHistoricQuery({
      runKeys: [runId],
      nodes: [graphViewGroupNodeLabelToSampleNode(node.label)],
    })

  const nodeResults = getNodeResults(historicResponse?.results, runId, node.label)

  const columns = useMemo(() => getMaterialTableColumns(), [])
  const rows = useMemo(() => getTableRows(nodeResults, node.label), [nodeResults, node.label])

  const isExportable = runData?.extra?.can_user_download_results
  const isFetching = isFetchingRunData || isFetchingHistoricData

  if (isFetching) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", my: 6 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!nodeResults) {
    logger.error(
      `RawResultsTable:: No results found for runId: "${runId}" and node: "${node.label}"`,
    )
    return (
      <Typography>
        Something went wrong. Sampling results for requested node could not be found
      </Typography>
    )
  }

  const handleExportTransform = (rows: NodeResultRow[]) => {
    // key of row will be used as a header in excel
    const excelRows: Record<string, string | number>[] = []

    const unitFormat = node.format || ""
    const unitHeaderKey = formatValuePythonic("", unitFormat)

    for (const row of rows) {
      for (const value of row.data) {
        excelRows.push({
          timestep: row.timestep,
          [`${row.node_key} ${unitHeaderKey}`]: formatValuePythonic(value, unitFormat),
        })
      }
    }

    const startTimeStep = rows[0]?.timestep ?? ""
    const endTimeStep = rows[rows.length - 1]?.timestep ?? ""
    const fileName = `${runId}-${node.label}-${startTimeStep}-${endTimeStep}`

    return { data: excelRows, csvConfig: { filename: fileName } }
  }

  return (
    <Box sx={{ mb: 4 }}>
      <Table
        sessionStorageName={"rawResultsTable"}
        isExportable={isExportable}
        isExportableRows={false}
        enableRowSelection
        enableGrouping
        exportTransformFn={handleExportTransform}
        groupedColumnMode="remove"
        initialState={{
          columnOrder: ["mrt-row-select", "mrt-row-expand", "node_key", "timestep", "data"],
          columnVisibility: { node_key: false },
        }}
        data={rows.slice(lagShift)}
        columns={columns}
        renderDetailPanel={({ row }) => {
          let datatitle = formatValuePythonic("", node.format)
          if (datatitle == "") datatitle = "data"

          return (
            <Box sx={{ width: "100%" }}>
              <Table
                export_label={`Export data for ${row.original.timestep}`}
                sessionStorageName={"rawResultsDetailTable"}
                isExportable={isExportable}
                isExportableSelection={false}
                isExportableRows={false}
                enableGlobalFilter={false}
                data={row.original.data.map((item) => {
                  const obj: Record<string, string> = {
                    node_key: row.original.node_key,
                    timestep: row.original.timestep,
                  }
                  obj[datatitle] = item.toString()
                  return obj
                })}
                columns={[
                  {
                    accessorKey: "node_key",
                    header: "Node",
                    size: 200,
                  },
                  {
                    accessorKey: "timestep",
                    header: "Timestep",
                    size: 200,
                  },
                  {
                    accessorKey: datatitle,
                    header: datatitle,
                    grow: true,
                  },
                ]}
                paginationDisplayMode="pages"
              />
            </Box>
          )
        }}
      />
    </Box>
  )
}
